import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";

import LoginPage1 from "./components/LoginPage1";
import MaterialTable from "./components/MaterialTable";
import CustomerTable from "./components/CustomerTable";
import ShowroomTable from "./components/ShowroomTable";
import BlockTable from "./components/BlockTable";
import EndProductsTab from "./components/EndProductsTab";
import RefaqHome, { RefaqStock } from "./components/RefaqHome";
import { COMPANIES, companyCodesForUser, companyForUser, normalizeCompany } from "./utils/companies";
import SalesTable from "./components/SalesTable";
import SalesOverview from "./components/SalesOverview";
import FinishedSales from "./components/FinishedSales";
import FinishedReservations from "./components/FinishedReservations";
import SalesTabs from "./components/SalesTabs";
import PaymentTable from "./components/PaymentTable";
import ReservationTable from "./components/ReservationTable";
import EmployeeTable from "./components/EmployeeTable";
import BlockSummaryView from "./components/BlockSummaryView";
import CustomerDetails from "./components/CustomerDetails";
import WelcomeTab from "./components/WelcomeTab";
import logo from "./assets/PdfImagetrs.png";
import RefaqLogo from "./components/RefaqLogo";
import StockAllShowroomsTab from "./components/StockAllShowroomsTab";
import DebtsTable from "./components/DebtsTable";
import BlockTransferTab from "./components/BlockTransferTab";
import TreatmentTab from "./components/TreatmentTab";
import DailyCashTab from "./components/DailyCashTab";

/* =========================
   1) تعريف الأدوار والهيلبرز
   ========================= */
const ROLE_ORDER = { employee: 1, admin: 2, owner: 3 };
function normRole(r) {
  return String(r || "").toLowerCase();
}
function roleRank(r) {
  return ROLE_ORDER[normRole(r)] || 0;
}
function hasAtLeast(userRole, requiredRole) {
  return roleRank(userRole) >= roleRank(requiredRole);
}

/* =======================================
   2) جميع التابات مع أقل دور مسموح requiredRole
   ======================================= */
const abbasTabs = [
  { name: "Home", component: WelcomeTab, requiredRole: "employee" },
  { name: "All inventory", component: StockAllShowroomsTab, requiredRole: "admin" },
  { name: "Materials", component: MaterialTable, requiredRole: "admin" },
  { name: "Customers/Suppliers", component: CustomerTable, requiredRole: "employee" },
  { name: "Showrooms", component: ShowroomTable, requiredRole: "admin" },
  { name: "Blocks and slabs", component: BlockTable, requiredRole: "employee" },
  { name: "Sales", component: SalesTable, requiredRole: "employee" },
  { name: "Sales overview", component: SalesOverview, requiredRole: "employee" },
  { name: "Reservations", component: ReservationTable, requiredRole: "employee" },
  { name: "Sales report", component: SalesTabs, requiredRole: "admin" },
  { name: "Payments", component: PaymentTable, requiredRole: "employee" },
  { name: "Block summary", component: BlockSummaryView, requiredRole: "employee" },
  { name: "Employees", component: EmployeeTable, requiredRole: "owner" },
  { name: "Outstanding balances", component: DebtsTable, requiredRole: "admin" },
  { name: "Stock transfers", component: BlockTransferTab, requiredRole: "admin" },
 { name: "Treatment", component: TreatmentTab, requiredRole: "admin" },
  { name: "Daily journal", component: DailyCashTab, requiredRole: "employee" },
];
const refaqTabs = [
  { name: "Home", component: RefaqHome, requiredRole: "employee" },
  { name: "Products", component: EndProductsTab, requiredRole: "employee" },
  { name: "Product inventory", component: RefaqStock, requiredRole: "admin" },
  { name: "Customers", component: CustomerTable, requiredRole: "employee" },
  { name: "Sales", component: FinishedSales, requiredRole: "employee" },
  { name: "Reservations", component: FinishedReservations, requiredRole: "employee" },
  { name: "Sales overview", component: SalesOverview, requiredRole: "employee" },
  { name: "Materials", component: MaterialTable, requiredRole: "admin" },
  { name: "Showrooms", component: ShowroomTable, requiredRole: "admin" },
  { name: "Employees", component: EmployeeTable, requiredRole: "owner" },
];

/* ================================
   3) تابات الـ Owner المسموح بها فقط
   ================================ */
const OWNER_ONLY_TABS = new Set(["Outstanding balances", "Sales report", "Sales overview", "All inventory", "Employees", "Daily journal"]);

/* زرار ستايل */
const btnStylePrimary = {
  padding: "10px 28px",
  borderRadius: 8,
  fontWeight: "bold",
  backgroundColor: "var(--accent, #514536)",
  color: "#faf8f4",
  border: "none",
  cursor: "pointer",
  fontSize: 16,
};
const btnStyleSecondary = {
  ...btnStylePrimary,
  backgroundColor: "#716759",
};
const btnStyleNeutral = {
  ...btnStylePrimary,
  backgroundColor: "#716759",
};

/* وِرابّير بسيط يمرّر user للكومبوننت */
function TabWrapper({ Comp, user, companyCode }) {
  return <div className="workspace-page"><Comp user={user} companyCode={companyCode} /></div>;
}

/* ======================
   4) الهيكل الرئيسي
   ====================== */
function MainLayout({ user, setUser, mode, setMode, companyCode, setCompanyCode }) {
  const [selectedTab, setSelectedTab] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // الدور الفعلي للوضع الحالي (mode) إن وجد، وإلا نستخدم الدور الحقيقي من قاعدة البيانات
  const effectiveRole = normRole(mode || user.role);
  const realRole = normRole(user.role);
  const company = COMPANIES[companyCode];
  const allowedCompanies = companyCodesForUser(user);
  const allTabs = companyCode === "refaq" ? refaqTabs : abbasTabs;

  // فلترة أساسية حسب الدورين (الوضع الحالي + الدور الحقيقي)
  let visibleTabs = allTabs.filter(
    (tab) =>
      hasAtLeast(effectiveRole, tab.requiredRole) &&
      hasAtLeast(realRole, tab.requiredRole)
  );

  // لو الوضع الحالي Owner، قيّد التابات على اللستة المحددة فقط
  if (effectiveRole === "owner") {
    visibleTabs = visibleTabs.filter((tab) => companyCode === "refaq" ? ["Product inventory", "Sales overview", "Reservations", "Employees"].includes(tab.name) : OWNER_ONLY_TABS.has(tab.name));
  }

  // حماية من الخروج عن المدى
  const safeTab = visibleTabs[selectedTab] || visibleTabs[0];

  function handleTabSelect(idx) {
    setSelectedTab(idx);
    if (location.pathname !== "/") {
      navigate("/");
    }
  }
  function changeCompany(code) {
    if (!allowedCompanies.includes(code)) return;
    setCompanyCode(normalizeCompany(code));
    setSelectedTab(0);
    navigate("/");
  }

  const canOwner = hasAtLeast(user.role, "owner");
  const canAdmin = hasAtLeast(user.role, "admin");

  // شاشة اختيار الـ mode للـ owner/admin فقط
  if (!mode) {
    if (!canAdmin) {
      // موظف فقط — هيتم ضبط الـ mode تلقائياً في App.useEffect
    } else {
      return (
        <div
          dir="ltr"
          className="auth-screen"
          style={{
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontFamily: "Jost, 'Trebuchet MS', Arial, sans-serif",
            background: "var(--canvas, #f2ede4)",
            padding: 20,
          }}
        >
          <div
            className="auth-card workspace-mode"
            style={{
              background: "#faf8f4",
              padding: 30,
              borderRadius: 14,
              boxShadow: "0 0 40px #51453633",
              textAlign: "center",
              maxWidth: 420,
              width: "100%",
            }}
          >
            <h2 style={{ color: "var(--accent, #514536)", marginBottom: 24 }}>
              Welcome {user.name}
            </h2>
            <p style={{ fontSize: 16, marginBottom: 20 }}>Choose your workspace mode?</p>
            <div
              className="auth-actions"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              {/* Owner: يظهر له Owner + موظف فقط (نخفي Admin للملاك) */}
              {canOwner && (
                <button onClick={() => setMode("owner")} style={btnStylePrimary}>
                  Owner
                </button>
              )}
              {!canOwner && canAdmin && (
                <button
                  onClick={() => setMode("admin")}
                  style={btnStyleSecondary}
                >
                  Admin
                </button>
              )}
              <button onClick={() => setMode("employee")} style={btnStyleNeutral}>
                Employee
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div
      dir="ltr"
      className="app-shell"
      style={{ "--accent": company.color, "--accent-soft": company.softColor } as React.CSSProperties}
    >
      {/* الشريط الجانبي */}
      <aside className="app-sidebar">
        {companyCode === "abbas_halim" && <img
          src={logo}
          alt="Abbas Halim logo"
          style={{ width: "45%", marginBottom: 30, borderRadius: 12 }}
        />}
        {companyCode === "refaq" && <RefaqLogo />}
        <div className="company-brand">
          <h2>{company.name}</h2>
          <p>{company.description}</p>
        </div>
        <label className="company-switcher">
          Current company
          <select aria-label="Current company" value={companyCode} onChange={(event) => changeCompany(event.target.value)}>
            {allowedCompanies.map((code) => COMPANIES[code]).map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
          </select>
        </label>
        <div className="staff-summary">
          <span className="staff-avatar" aria-hidden="true">{String(user.name || "Staff").trim().split(/\s+/).slice(0,2).map(part => part[0]).join("").toUpperCase()}</span>
          <div><strong>{user.name}</strong><small>{{ owner: "Owner", admin: "Admin", employee: "Employee" }[effectiveRole] || "Employee"}</small></div>
        </div>

        <nav className="app-nav" aria-label="Company sections">
        {visibleTabs.map((tab, idx) => (
          <button
            key={tab.name}
            onClick={() => handleTabSelect(idx)}
            aria-current={safeTab?.name === tab.name ? "page" : undefined}
          >
            {tab.name}
          </button>
        ))}
        </nav>

        <button
          className="sign-out"
          onClick={() => {
            setUser(null);
            setMode(null);
            sessionStorage.removeItem("user");
            sessionStorage.removeItem("mode");
            sessionStorage.removeItem("company");
            setCompanyCode("abbas_halim");
          }}
        >
          Sign out
        </button>
      </aside>

      {/* محتوى الصفحة + Routing */}
      <main className="app-content">
        <p className="app-breadcrumb"><span>Abbas Halim Group</span><span aria-hidden="true">/</span><span>{company.name}</span><span aria-hidden="true">/</span><span>{safeTab?.name}</span></p>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <h1 className="app-heading">
                  {safeTab?.name || ""}
                </h1>
                <div className="workspace-panel">
                  {safeTab ? (
                    <TabWrapper Comp={safeTab.component} user={user} companyCode={companyCode} />
                  ) : (
                    <div>No sections are available in this mode.</div>
                  )}
                </div>
              </>
            }
          />
          {companyCode === "abbas_halim" && <Route path="/customer/:id" element={<div className="workspace-panel workspace-page customer-page"><CustomerDetails user={user} /></div>} />}
          <Route path="*" element={<div>Choose a section from the company menu.</div>} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(() => {
    const savedUser = sessionStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [mode, setMode] = useState(() => {
    return sessionStorage.getItem("mode") || null;
  });
  const [companyCode, setCompanyCode] = useState(() => normalizeCompany(sessionStorage.getItem("company")));
  const activeCompanyCode = companyForUser(user, companyCode);
  useEffect(() => {
    if (user && activeCompanyCode) sessionStorage.setItem("company", activeCompanyCode);
    else sessionStorage.removeItem("company");
  }, [activeCompanyCode, user]);

  // حفظ user و mode في sessionStorage عند تغيّرهم
  useEffect(() => {
    if (user) {
      sessionStorage.setItem("user", JSON.stringify(user));
    } else {
      sessionStorage.removeItem("user");
    }
  }, [user]);

  useEffect(() => {
    if (mode) {
      sessionStorage.setItem("mode", mode);
    } else {
      sessionStorage.removeItem("mode");
    }
  }, [mode]);

  // Auto-mode: لو موظف فقط، دخّليه كـ employee تلقائيًا
  useEffect(() => {
    if (user && !mode) {
      const r = normRole(user.role);
      if (hasAtLeast(r, "owner")) {
        // هيظهر اختيار الوضع في MainLayout (Owner: Owner/موظف)
      } else if (hasAtLeast(r, "admin")) {
        // هيظهر اختيار الوضع في MainLayout (Admin: Admin/موظف)
      } else {
        // موظف فقط
        setMode("employee");
      }
    }
  }, [user, mode]);

  if (!user) {
    return <LoginPage1 onLogin={setUser} />;
  }
  if (!activeCompanyCode) {
    return <div dir="ltr" style={{ padding: 36 }}><p>No company is assigned to this account. Contact the system owner.</p><button onClick={() => { setUser(null); setMode(null); setCompanyCode("abbas_halim"); }}>Sign out</button></div>;
  }

  return (
    <BrowserRouter>
      <MainLayout key={activeCompanyCode} user={user} setUser={setUser} mode={mode} setMode={setMode} companyCode={activeCompanyCode} setCompanyCode={setCompanyCode} />
    </BrowserRouter>
  );
}

export default App;
